import { Exclusions, useWizard } from '@insify/funnel-components';
import { useEffect, useState } from 'react';

import { useCommands } from '../../hooks/useCommands';
import { useExclusionsContent } from '../../hooks/useExclusionContent';
import { useConditionsQuery, useParamsQuery } from '../../hooks/useQueries';

import type { Commands } from '../../models/commands';
import type { Conditions, Declarations } from '../../models/queries';

export const ExclusionsPage = () => {
  const { showNextPage, showPreviousPage } = useWizard();
  const { sendMultipleCommands } = useCommands();
  const exclusionsContent = useExclusionsContent();
  const { data: quoteConditions } = useConditionsQuery();
  const [commandsSent, setCommandsSent] = useState(false);
  const { isStale, data = {} } = useParamsQuery();

  // At least one is answered, otherwise we would have skipped this page
  const exclusionCheck =
    data?.activeClaims === true ||
    data?.segmentSpecificExclusions === true ||
    data?.activitySpecificFieldAnswers?.productCertification === true ||
    (data?.declarations?.length ?? 0) !== 0;

  useEffect(() => {
    if (commandsSent && !isStale && exclusionCheck) {
      showNextPage();
      setCommandsSent(false);
    }
  }, [commandsSent, isStale, data]);

  const knownDelcarations: Declarations[] = [
    'hiscoxFrenchCompany',
    'hiscoxNoDoubleInsurance',
    'hiscoxNoPastOrPotentialClaims',
    'hiscoxSecuritySpecific',
    'hiscoxTourismSpecific',
  ];

  return (
    <Exclusions
      {...exclusionsContent}
      isSubmitting={commandsSent}
      eventHandlers={{
        onBack: showPreviousPage,
        onSubmit: ({ activeClaimAwareness, exclusionsSegmentSpecific, ...rest }) => {
          const commands: Commands[] = [];

          if (hasShownProductCertificationAwareness(quoteConditions)) {
            commands.push({
              type: 'confirmProductCertificationAwareness',
              payload: { awareness: true },
            });
          }

          if (activeClaimAwareness) {
            commands.push({
              type: 'confirmActiveClaimsAwareness',
              payload: { awareness: true },
            });
          }

          const declarations = Object.keys(rest).filter((declaration) =>
            knownDelcarations.includes(declaration as Declarations),
          );

          if (declarations.length !== 0) {
            commands.push({
              type: 'confirmDeclarations',
              payload: { declarations },
            });
          }

          if (exclusionsSegmentSpecific.length) {
            commands.push({
              type: 'confirmSegmentSpecificExclusionsAwareness',
              payload: {
                exclusions: exclusionsSegmentSpecific,
                awareness: true,
              },
            });
          }

          void sendMultipleCommands(commands);
          setCommandsSent(true);
        },
      }}
    />
  );
};

const hasShownProductCertificationAwareness = (quoteConditions: Partial<Conditions> | undefined) =>
  quoteConditions?.activitySpecificFieldDescriptions?.find((d) => d.key === 'productCertification');
